import React from "react";
import { useTranslation } from "react-i18next";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { Typography, useTheme } from "@mui/material";
import styled from "@emotion/styled";
import { useListAndSearchAllTransactionV2 } from "@rentablo/api/apiv1";
import { toCurrency } from "@helpers";
import { NoAccountsMiniCard } from "@components/Card";
import { useDashboardSelectedGroupsContext } from "@features/dashboard/DashboardSelectedGroupProvider.tsx";
import { LoadingPlaceHolder } from "@rentablo/components";
import { MiniCard } from "@rentablo/components";

const TransactionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Transactions = styled.div`
  width: 100%;
`;

const TransactionName = styled(Typography)`
  max-width: 250px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export interface CreditCardsCardProps {
  currency: string;
  onAddAccountsClick: () => void;
  onClick: () => void;
}

export const CreditCardsCard = ({
  currency,
  onAddAccountsClick,
  onClick,
}: CreditCardsCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    groupedAccountIds,
    accounts,
    isFetching: areAccountsLoading,
  } = useDashboardSelectedGroupsContext();
  const creditCardIds = groupedAccountIds?.creditCardIds;
  const creditCardAccounts = accounts?.filter(
    ({ typeFromBank }) => typeFromBank === "CREDIT_CARD"
  );

  const enabled = creditCardIds?.length > 0;
  const { data: transactionsResponse, isFetching } =
    useListAndSearchAllTransactionV2(
      {
        accountId: creditCardIds,
        perPage: 1,
        order: ["bankBookingDate desc"],
        displayCurrency: currency,
      },
      {
        enabled,
      }
    );
  const [lastTransaction] = transactionsResponse?.transactions ?? [];
  const balance = creditCardAccounts?.reduce(
    (sum, { balance }) => sum + (balance ?? 0),
    0
  );

  if ((enabled && isFetching) || areAccountsLoading) {
    return <LoadingPlaceHolder />;
  }

  const transactionName = lastTransaction?.purpose ?? "Not available";
  const transactionValue = lastTransaction?.amount;

  return (
    <>
      {creditCardIds.length === 0 && (
        <NoAccountsMiniCard
          onClick={onAddAccountsClick}
          icon={<CreditCardIcon fontSize="small" width="24px" height="24px" />}
          title={t("domains.creditCardCard.noAccountsTitle")}
          subtitle={t("domains.creditCardCard.noAccountsSubtitle")}
          buttonText={t("domains.creditCardCard.noAccountsButtonText")}
        />
      )}
      {creditCardIds.length > 0 && (
        <MiniCard
          icon={<CreditCardIcon fontSize="small" width="24px" height="24px" />}
          title={t("domains.creditCardCard.title")}
          headline={toCurrency(balance, currency)}
          onClick={onClick}
        >
          <Transactions>
            <Typography
              variant="overline"
              fontWeight="600"
              color="textSecondary"
            >
              {t("domains.creditCardsMiniCard.lastTransaction")}
            </Typography>
            {transactionName && transactionValue ? (
              <TransactionRow>
                <TransactionName variant="body3">
                  {transactionName}
                </TransactionName>
                <Typography
                  variant="body1"
                  noWrap
                  color="textSecondary"
                  sx={{ flexShrink: 0, marginLeft: theme.spacing(1) }}
                >
                  {toCurrency(transactionValue, currency, true).replace(
                    /\s/,
                    ""
                  )}
                </Typography>
              </TransactionRow>
            ) : (
              <Typography variant="body3">
                {t("domains.creditCardsMiniCard.noTransactions")}
              </Typography>
            )}
          </Transactions>
        </MiniCard>
      )}
    </>
  );
};
